import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DialogRef } from '@ngneat/dialog';
import { EventsComposition } from '@shared/compositions/events.composition';
import { EventEntity, EventSelectedDates, RecurringDate } from '@core/entities';
import { ButtonComponent } from '@shared/components/ui';
import { IconComponent } from '@shared/components/ui/icon/icon.component';
import { GetUserId, isMobileView } from '@core/utils';
import { RRule } from 'rrule';

interface EventDatesSelectionDialogData {
  event: EventEntity;
}

@Component({
  selector: 'app-event-dates-selection',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ButtonComponent, IconComponent],
  templateUrl: './event-dates-selection.component.html',
  styleUrls: ['./event-dates-selection.component.scss'],
})
export class EventDatesSelectionComponent extends EventsComposition implements OnInit {
  form: FormGroup;
  availableDates: RecurringDate[] = []; // Changed to use RecurringDate for date ranges
  event: EventEntity;
  protected readonly isMobileView = isMobileView();
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _dialogRef: DialogRef;

  constructor() {
    super();

    if (!isMobileView()) {
      this._dialogRef = inject(DialogRef);
    }

    this.form = this._fb.group({
      selectedDates: this._fb.array([]),
    });
  }

  get selectedDatesArray(): FormArray {
    return this.form.get('selectedDates') as FormArray;
  }

  ngOnInit(): void {
    const data = isMobileView() ? (this._bottomSheetService.currentSheetData as EventDatesSelectionDialogData) : (this._dialogRef.data as EventDatesSelectionDialogData);
    if (data && data.event) {
      this.event = data.event;
      this._loadAvailableDateRanges();
      this._initializeCheckboxes();
    } else {
      console.error('EventDatesSelectionDialogComponent: No event data provided');
    }
  }

  /**
   * Check if the current user has already joined a specific date range
   */
  isUserAlreadyJoinedForDateRange(dateRange: RecurringDate): boolean {
    const currentUserId = +GetUserId();

    if (!this.event?.selectedDates || !currentUserId) {
      return false;
    }

    // Normalize dates to compare only the date part
    const normalizeDate = (dateStr: string): string => {
      const date = new Date(dateStr);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const rangeStart = normalizeDate(dateRange.dateFrom);
    const rangeEnd = normalizeDate(dateRange.dateEnd);

    // Check if user has joined this exact date range
    return this.event.selectedDates.some((selectedDate) => {
      const isSameUser = selectedDate.userId === currentUserId;
      const selectedStart = normalizeDate(selectedDate.startDate);
      const selectedEnd = normalizeDate(selectedDate.endDate);

      return isSameUser && selectedStart === rangeStart && selectedEnd === rangeEnd;
    });
  }

  /**
   * Check if a specific date range index is already joined by current user
   */
  isDateRangeAlreadyJoined(index: number): boolean {
    return this.isUserAlreadyJoinedForDateRange(this.availableDates[index]);
  }

  toggleDate(index: number): void {
    // Prevent toggling if user has already joined this date range
    if (this.isDateRangeAlreadyJoined(index)) {
      return;
    }

    const control = this.selectedDatesArray.at(index);
    control.setValue(!control.value);
  }

  formatDateDisplay(dateRange: RecurringDate): string {
    if (dateRange.dateFrom === dateRange.dateEnd) {
      // Single date
      const date = new Date(dateRange.dateFrom);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      const dateFormatted = formatDate(dateRange.dateFrom, 'dd.MM.yyyy', 'en-US');
      return `${this._translate.instant(dayName.toLowerCase())} - ${dateFormatted}`;
    }

    // Date range
    // const startDate = new Date(dateRange.dateFrom);
    // const endDate = new Date(dateRange.dateEnd);
    const startDateFormatted = formatDate(dateRange.dateFrom, 'dd.MM.yyyy', 'en-US');
    const endDateFormatted = formatDate(dateRange.dateEnd, 'dd.MM.yyyy', 'en-US');

    return `${startDateFormatted} - ${endDateFormatted}`;
  }

  getEventTimeForDateRange(dateRange: RecurringDate): { startTime: string; endTime: string } {
    return {
      startTime: dateRange.startTime,
      endTime: dateRange.endTime,
    };
  }

  getSelectedDates(): EventSelectedDates[] {
    const selectedIndices: number[] = [];

    this.selectedDatesArray.controls.forEach((control, index) => {
      if (control.value) {
        selectedIndices.push(index);
      }
    });

    // Convert RecurringDate to EventSelectedDates format
    return selectedIndices.map((index: number) => {
      const dateRange = this.availableDates[index];
      return {
        startDate: dateRange.dateFrom,
        endDate: dateRange.dateEnd,
      };
    });
  }

  hasSelectedDates(): boolean {
    return this.selectedDatesArray.controls.some((control) => control.value);
  }

  /**
   * Get only newly selected date ranges (excluding already joined ones)
   */
  getNewlySelectedDates(): EventSelectedDates[] {
    const newlySelectedIndices: number[] = [];

    this.selectedDatesArray.controls.forEach((control, index) => {
      if (control.value && control.enabled) {
        newlySelectedIndices.push(index);
      }
    });

    // Convert to EventSelectedDates format
    return newlySelectedIndices.map((index: number) => {
      const dateRange = this.availableDates[index];
      return {
        startDate: dateRange.dateFrom,
        endDate: dateRange.dateEnd,
      };
    });
  }

  /**
   * Check if there are any newly selected date ranges (excluding already joined)
   */
  hasNewlySelectedDates(): boolean {
    return this.selectedDatesArray.controls.some((control) => control.value && control.enabled);
  }

  selectAll(): void {
    this.selectedDatesArray.controls.forEach((control) => {
      if (control.enabled) {
        control.setValue(true);
      }
    });
  }

  selectNone(): void {
    this.selectedDatesArray.controls.forEach((control) => {
      if (control.enabled) {
        control.setValue(false);
      }
    });
  }

  join(btnRef?: ButtonComponent): void {
    const newlySelectedDates = this.getNewlySelectedDates();

    if (newlySelectedDates.length === 0) {
      this._toast.warning(this._translate.instant('Please select at least one new date'));
      return;
    }

    this.joinEventsWithSelectedDates(this.event, newlySelectedDates, btnRef);
  }

  close(): void {
    if (isMobileView()) {
      this._bottomSheetService.close();
      return;
    }
    this._dialogRef.close(false);
  }

  /**
   * Load available date ranges from recurringDates or recurrence string, filtering out past dates
   */
  private _loadAvailableDateRanges(): void {
    this.availableDates = [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Case 1: Event has recurrence string (RRule)
    if (this.event?.recurrence && !this.event.recurrence.includes('null')) {
      try {
        const rruleSet = RRule.fromString(this.event.recurrence);
        const startDates = rruleSet.all();

        // Convert RRule dates to RecurringDate format
        startDates.forEach((startDate: Date) => {
          const dateStr = startDate.toISOString().split('T')[0];
          const startTime = this.event.recurringDates?.[0]?.startTime || '00:00';
          const endTime = this.event.recurringDates?.[0]?.endTime || '00:00';

          // Only include future dates
          const eventDate = new Date(dateStr);
          eventDate.setHours(0, 0, 0, 0);

          if (eventDate >= today) {
            this.availableDates.push({
              dateFrom: dateStr,
              dateEnd: dateStr, // Single date for RRule
              startTime: startTime,
              endTime: endTime,
            });
          }
        });
      } catch (error) {
        console.error('Error parsing RRule:', error);
      }
    }
    // Case 2: Event has recurringDates array
    else if (this.event?.recurringDates?.length) {
      // Filter out past date ranges
      this.availableDates = this.event.recurringDates.filter((dateRange) => {
        const endDate = new Date(dateRange.dateEnd);
        endDate.setHours(0, 0, 0, 0);
        return endDate >= today;
      });
    }
    // Case 3: Single event with dateFrom/dateTo
    else if (this.event?.dateFrom) {
      const eventDate = new Date(this.event.dateFrom.split('T')[0]);
      eventDate.setHours(0, 0, 0, 0);

      if (eventDate >= today) {
        const startDate = this.event.dateFrom.split('T')[0];
        const endDate = this.event.dateTo ? this.event.dateTo.split('T')[0] : startDate;

        this.availableDates.push({
          dateFrom: startDate,
          dateEnd: endDate,
          startTime: this.event.recurringDates?.[0]?.startTime || '00:00',
          endTime: this.event.recurringDates?.[0]?.endTime || '00:00',
        });
      }
    }
  }

  private _initializeCheckboxes(): void {
    this.availableDates.forEach((dateRange) => {
      const isAlreadyJoined = this.isUserAlreadyJoinedForDateRange(dateRange);
      this.selectedDatesArray.push(
        this._fb.control({
          value: isAlreadyJoined,
          disabled: isAlreadyJoined,
        }),
      );
    });
  }
}
